import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getCurrentCustomer,
    nextTurnInQueue,
    endCurrentCustomer,
} from "../../../services/employee";
import { getEmployeeServices } from "../../../services/employee-services";
import { getEmployeeBookings } from "../../../services/appointments";
import {
    connectToSocket,
    emitSocketEvent,
    onSocketEvent,
} from "../../../services/socket";
import { useAuth } from "../../../context/AuthContext";
import { CircularProgress } from "@mui/material";
import { motion } from "framer-motion";
import Table from "../../../components/Table/Table";

const EmployeeDashboard = () => {
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const [queues, setQueues] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [currentCustomer, setCurrentCustomer] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeQueue, setActiveQueue] = useState(null);
    const [isCardUpdating, setIsCardUpdating] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await getEmployeeServices(user.id);
                const data = response.data;

                const queueServices = data
                    .filter((s) => s.reservationType === "queue")
                    .map((q) => ({
                        id: q.id,
                        name: q.name,
                        isActive: q.status === "on",
                        queueId: q.queue?.queueId,
                        waitingCount: q.queue?.waitingCount ?? 0,
                        waitingTime: q.queue?.waitingCount * 2 || 0,
                    }));

                const appointmentServices = data
                    .filter((s) => s.reservationType === "appointment")
                    .map((a) => ({
                        id: a.id,
                        name: a.name,
                        status: a.status,
                    }));

                const res = await getCurrentCustomer(token);
                const servedCustomer = res.data?.nextTurn;

                const bookingsRes = await getEmployeeBookings(token);
                const formattedBookings = (bookingsRes?.data || []).map((b) => ({
                    id: b.id,
                    date: b.date,
                    dayName: b.dayName,
                    time: `${b.startTime} - ${b.endTime}`,
                    status: b.status === "pending" ? "قيد الانتظار" : b.status === "completed" ? "مكتمل" : "ملغي",
                    service: b.service?.name || "",
                    branch: b.branch?.name || "",
                    institution: b.institution?.name || "",
                    customer: b.customer?.name || "",
                }));

                setBookings(formattedBookings);
                setCurrentCustomer(servedCustomer);
                setQueues(queueServices);
                setAppointments(appointmentServices);

                const mostCrowded = [...queueServices]
                    .filter((q) => q.waitingCount > 0)
                    .sort((a, b) => b.waitingCount - a.waitingCount)[0];

                const currentQueue =
                    servedCustomer &&
                    queueServices.find((q) => q.queueId === servedCustomer.queueId);

                setActiveQueue(currentQueue || mostCrowded || null);

                connectToSocket();
                queueServices.forEach((q) => {
                    if (q.queueId) emitSocketEvent("join-queue-room", { queueId: q.queueId });
                });

                onSocketEvent("queue:stats:update", (data) => {
                    setQueues((prev) => {
                        const updatedQueues = prev.map((q) =>
                            q.queueId === data.queueId
                                ? {
                                    ...q,
                                    waitingCount: data.waitingCount,
                                    waitingTime: data.estimatedWaitingTime,
                                }
                                : q
                        );

                        const mostCrowded = [...updatedQueues]
                            .filter((q) => q.waitingCount > 0)
                            .sort((a, b) => b.waitingCount - a.waitingCount)[0];

                        const currentQueue = currentCustomer
                            ? updatedQueues.find((q) => q.queueId === currentCustomer.queueId)
                            : null;

                        setActiveQueue(currentCustomer ? currentQueue : mostCrowded || null);

                        return updatedQueues;
                    });
                });
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    const formatPhoneNumber = (phoneNumber) => {
        const phoneString = phoneNumber.toString();
        if (phoneString.length === 10) {
            return `(${phoneString.slice(0, 3)}) ${phoneString.slice(3, 6)}-${phoneString.slice(6)}`;
        }
        return phoneString;  // Return as-is if the format doesn't match (you can customize more if needed)
    };

    const styles = {
        card: {
            background: "white",
            borderRadius: "var(--radius)",
            padding: 20,
            border: "1px solid var(--clr-border)",
            minHeight: 230,
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 10,
            position: "relative",
        },
        cardHeader: {
            display: "flex",
            justifyContent: "space-between",
            fontSize: 16,
            fontWeight: 500,
            color: "var(--clr-text-main)",
        },
        statusBadge: (on) => ({
            background: on ? "#d1f7f0" : "#fdeaea",
            color: on ? "var(--clr-primary-d)" : "var(--clr-error)",
            padding: "2px 10px",
            borderRadius: 12,
            fontSize: 13,
        }),
        bigNum: {
            fontSize: 36,
            fontWeight: 600,
            color: "var(--clr-text-main)",
            textAlign: "center",
        },
        sub: {
            fontSize: 14,
            color: "var(--clr-text-secondary)",
            textAlign: "center",
        },
        statBox: {
            display: "flex",
            justifyContent: "space-between",
            fontSize: 14,
            padding: "6px 12px",
            background: "#f9f9f9",
            borderRadius: "var(--radius)",
        },
        progressWrap: {
            height: 6,
            background: "#eee",
            borderRadius: 4,
            overflow: "hidden",
        },
        progressFill: (count) => {
            let color = "green";
            if (count >= 15) color = "red";
            else if (count >= 8) color = "orange";
            return {
                height: "100%",
                width: `${Math.min((count / 10) * 100, 100)}%`,
                background: color,
            };
        },
        loaderOverlay: {
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "var(--radius)",
        },
    };

    const columns = [
        { key: "date", label: "التاريخ" },
        { key: "dayName", label: "اليوم" },
        { key: "time", label: "الوقت" },
        { key: "status", label: "الحالة" },
        { key: "service", label: "الخدمة" },
        { key: "branch", label: "الفرع" },
        { key: "institution", label: "المؤسسة" },
        { key: "customer", label: "العميل" },
    ];

    const handleCancelAppointment = (id) => {
        setBookings((prev) =>
            prev.map((a) => (a.id === id ? { ...a, status: "ملغي" } : a))
        );
    };

    return (
        <div className="page">
            <div className="content-view">
                <h2>👋 مرحباً {user.name || "-"}</h2>

                {/* إدارة الطوابير */}
                <div style={{ marginTop: 30 }}>
                    <h3 style={{ fontSize: 20, marginBottom: 15 }}>إدارة الطوابير</h3>

                    <motion.div layout style={styles.card}>
                        {isCardUpdating && (
                            <div style={styles.loaderOverlay}>
                                <CircularProgress size={30} />
                            </div>
                        )}

                        {activeQueue ? (
                            <>
                                <div style={styles.cardHeader}>
                                    <span>{activeQueue.name}</span>
                                    <span style={styles.statusBadge(activeQueue.isActive)}>
                                        {activeQueue.isActive ? "نشط" : "متوقف"}
                                    </span>
                                </div>

                                {currentCustomer ? (
                                    <>
                                        <div style={styles.bigNum}>#{currentCustomer.customerTurn}</div>
                                        <div style={styles.sub}>🟢 يتم الآن خدمة العميل</div>
                                        <div style={{ fontSize: 16, fontWeight: 500, marginTop: "10px" }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: "#333" }}>
                                                <strong>العميل: </strong>{currentCustomer.customer.name}
                                            </div>
                                            <div style={{ fontSize: 16, fontWeight: 500, color: "#555", marginTop: "5px" }}>
                                                <strong>رقم الهاتف: </strong>
                                                <a
                                                    href={`tel:${currentCustomer.customer.phoneNumber}`}
                                                    style={{ color: "#1a73e8", textDecoration: "none" }}
                                                >
                                                    +{formatPhoneNumber(currentCustomer.customer.phoneNumber)}
                                                </a>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div style={styles.bigNum}>—</div>
                                        <div style={styles.sub}>لا يوجد عميل قيد الخدمة</div>
                                    </>
                                )}

                                <div style={styles.statBox}>
                                    <span>🧍‍♂️ {activeQueue.waitingCount} شخص</span>
                                    <span>{activeQueue.waitingTime} دقيقة</span>
                                </div>

                                <div style={styles.progressWrap}>
                                    <div style={styles.progressFill(activeQueue.waitingCount)} />
                                </div>
                            </>
                        ) : (
                            <div style={{ textAlign: "center", padding: "40px 0" }}>
                                <div style={styles.bigNum}>—</div>
                                <div style={styles.sub}>لا يوجد عملاء حالياً</div>
                            </div>
                        )}

                        <div style={{ display: "flex", gap: 10 }}>
                            <button
                                className="btn outline small demo"
                                disabled={!queues.some((q) => q.waitingCount > 0)}
                                onClick={async () => {
                                    setIsCardUpdating(true);
                                    const mostCrowded = [...queues]
                                        .filter((q) => q.waitingCount > 0)
                                        .sort((a, b) => b.waitingCount - a.waitingCount)[0];
                                    if (mostCrowded) {
                                        const res = await nextTurnInQueue(token, mostCrowded.queueId);
                                        setCurrentCustomer(res.data.nextTurn);
                                        const newQueue = queues.find((q) => q.queueId === res.data.nextTurn.queueId);
                                        setActiveQueue(newQueue);
                                    }
                                    setIsCardUpdating(false);
                                }}
                            >
                                ⏭️ خدمة التالي
                            </button>

                            {currentCustomer && (
                                <button
                                    className="btn outline small demo-err"
                                    onClick={async () => {
                                        setIsCardUpdating(true);
                                        await endCurrentCustomer(token, currentCustomer.queueId);
                                        setCurrentCustomer(null);
                                        const fallback = [...queues]
                                            .filter((q) => q.waitingCount > 0)
                                            .sort((a, b) => b.waitingCount - a.waitingCount)[0];
                                        setActiveQueue(fallback || null);
                                        setIsCardUpdating(false);
                                    }}
                                >
                                    ❌ إنهاء الدور
                                </button>
                            )}
                        </div>
                    </motion.div>
                </div>

                {/* جدول المواعيد */}
                <div style={{ marginTop: 50 }}>
                    <h3 style={{ fontSize: 20, marginBottom: 15 }}>📅 جدول المواعيد</h3>

                    <Table
                        data={bookings}
                        columns={columns}
                        loading={isLoading}
                        onDelete={handleCancelAppointment}
                        editableColumns={[]}
                        highlightConfig={{
                            status: {
                                "قيد الانتظار": "#facc15",
                                "مكتمل": "#01c8aa",
                                "ملغي": "#ef4444",
                            },
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default EmployeeDashboard;
